import {
  ACTIVITY_DIMENSION_NAMES,
  type ActivityDimensionName,
  type ActivityDimensions,
  type CodeDimensionConstraint,
  type CodeDimensionRule,
} from './activity-dimensions';

export const PRESENCE_UNCERTAIN_CONFIDENCE_PENALTY = 0.2;

export interface CodeRuleCatalogEntry {
  code: string;
  rule: CodeDimensionRule | null;
}

export type ResolvedCodeSource = 'rules' | 'model_fallback' | 'none';

export interface ResolveCodeInput {
  dimensions: ActivityDimensions;
  catalog: ReadonlyArray<CodeRuleCatalogEntry>;
  previousCode?: string | null;
  modelCode?: string | null;
}

export interface ResolvedCode {
  code: string | null;
  source: ResolvedCodeSource;
  confidencePenalty: number;
}

type DimensionValues = Record<ActivityDimensionName, string>;

interface Candidate {
  code: string;
  rule: CodeDimensionRule;
}

function effectiveValues(dimensions: ActivityDimensions): DimensionValues {
  return {
    presence: dimensions.presence.value === 'uncertain' ? 'patient_visible' : dimensions.presence.value,
    posture: dimensions.posture.value,
    room: dimensions.room.value,
    furniture: dimensions.furniture.value,
    wake: dimensions.wake.value === 'uncertain' ? 'awake' : dimensions.wake.value,
    activity: dimensions.activity.value,
  };
}

function allowsValue(allowed: string | string[], value: string): boolean {
  return Array.isArray(allowed) ? allowed.includes(value) : allowed === value;
}

function constraintAllows(constraint: CodeDimensionConstraint, value: string): boolean {
  if (typeof constraint === 'string' || Array.isArray(constraint)) {
    return allowsValue(constraint, value);
  }
  return !allowsValue(constraint.not, value);
}

function temporalMatches(rule: CodeDimensionRule, previousCode: string | null): boolean {
  if (!rule.temporal) {
    return true;
  }
  return previousCode !== null && rule.temporal.previousCodes.includes(previousCode);
}

function ruleMatches(rule: CodeDimensionRule, values: DimensionValues, previousCode: string | null): boolean {
  if (!temporalMatches(rule, previousCode)) {
    return false;
  }
  const implicitPresence = !rule.temporal && rule.requires.presence === undefined;
  if (implicitPresence && values.presence !== 'patient_visible') {
    return false;
  }
  return ACTIVITY_DIMENSION_NAMES.every((name) => {
    const constraint = rule.requires[name];
    return constraint === undefined || constraintAllows(constraint, values[name]);
  });
}

function constrainedDimensionCount(rule: CodeDimensionRule): number {
  return ACTIVITY_DIMENSION_NAMES.filter((name) => rule.requires[name] !== undefined).length;
}

function compareCandidates(left: Candidate, right: Candidate): number {
  if (left.rule.priority !== right.rule.priority) {
    return right.rule.priority - left.rule.priority;
  }
  const constrainedDifference = constrainedDimensionCount(right.rule) - constrainedDimensionCount(left.rule);
  if (constrainedDifference !== 0) {
    return constrainedDifference;
  }
  return left.code < right.code ? -1 : 1;
}

export function resolveCode(input: ResolveCodeInput): ResolvedCode {
  const confidencePenalty =
    input.dimensions.presence.value === 'uncertain' ? PRESENCE_UNCERTAIN_CONFIDENCE_PENALTY : 0;
  const values = effectiveValues(input.dimensions);
  const previousCode = input.previousCode ?? null;
  const candidates: Candidate[] = [];
  for (const entry of input.catalog) {
    if (entry.rule && ruleMatches(entry.rule, values, previousCode)) {
      candidates.push({ code: entry.code, rule: entry.rule });
    }
  }
  candidates.sort(compareCandidates);
  const winner = candidates[0];
  if (winner) {
    return { code: winner.code, source: 'rules', confidencePenalty };
  }
  const modelCode = input.modelCode ?? null;
  if (modelCode !== null && input.catalog.some((entry) => entry.code === modelCode)) {
    return { code: modelCode, source: 'model_fallback', confidencePenalty };
  }
  return { code: null, source: 'none', confidencePenalty };
}
